import { lazy, Suspense } from "react";
import { Route, Routes } from "react-router";

import { AreaLayout } from "@/components/area-layout";
import {
  AdminRoute,
  ClientRoute,
  ContributorRoute,
  FullPageLoader,
  NdaRoute,
  ProtectedRoute,
  RoleHomeRedirect,
  StaffRoute,
} from "@/components/protected-route";
import { AcceptInvitePage } from "@/features/accept-invite";
import { AdminActivityPage } from "@/features/admin/activity";
import { AdminUsersPage } from "@/features/admin/users";
import { AdminWorkspacesPage } from "@/features/admin/workspaces";
import { AdminWorkspacePage } from "@/features/admin/workspaces/workspace-detail";
import { ForgotPasswordPage } from "@/features/forgot-password";
import { LoginPage } from "@/features/login";
import { NotFoundPage } from "@/features/not-found";
import { ProfilePage } from "@/features/profile";
import { ResetPasswordPage } from "@/features/reset-password";
import { VerifyEmailPage } from "@/features/verify-email";

// Each area loads as its own chunk, so a contributor's phone never downloads the cockpit
// or the portal, and a client never downloads the cockpit.
const CockpitPage = lazy(() =>
  import("@/features/cockpit").then((m) => ({ default: m.CockpitPage })),
);
const EngagementPage = lazy(() =>
  import("@/features/cockpit/engagement").then((m) => ({ default: m.EngagementPage })),
);
const IntakePage = lazy(() =>
  import("@/features/cockpit/intake").then((m) => ({ default: m.IntakePage })),
);
const LibraryPage = lazy(() =>
  import("@/features/cockpit/library").then((m) => ({ default: m.LibraryPage })),
);
const CoveragePage = lazy(() =>
  import("@/features/cockpit/coverage").then((m) => ({ default: m.CoveragePage })),
);
const DraftFlowPage = lazy(() =>
  import("@/features/cockpit/flow-editor").then((m) => ({ default: m.DraftFlowPage })),
);
const ContributorMapPage = lazy(() =>
  import("@/features/cockpit/flow-editor").then((m) => ({ default: m.ContributorMapPage })),
);
const PortalPage = lazy(() => import("@/features/portal").then((m) => ({ default: m.PortalPage })));
const PortalPeoplePage = lazy(() =>
  import("@/features/portal/people").then((m) => ({ default: m.PortalPeoplePage })),
);
const PortalEngagementPage = lazy(() =>
  import("@/features/portal/engagement").then((m) => ({ default: m.PortalEngagementPage })),
);
const PortalDocumentsPage = lazy(() =>
  import("@/features/portal/documents").then((m) => ({ default: m.PortalDocumentsPage })),
);
const PortalNdaPage = lazy(() =>
  import("@/features/portal/nda").then((m) => ({ default: m.PortalNdaPage })),
);
const AdminNdaPage = lazy(() =>
  import("@/features/admin/nda").then((m) => ({ default: m.AdminNdaPage })),
);
const ContributorArea = lazy(() =>
  import("@/features/contributor").then((m) => ({ default: m.ContributorArea })),
);

export function App() {
  return (
    <Routes>
      {/* Each role's home: BOI staff → /cockpit, clients → /portal, anonymous → /login. */}
      <Route path="/" element={<RoleHomeRedirect />} />
      <Route path="/home" element={<RoleHomeRedirect />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/accept-invite" element={<AcceptInvitePage />} />
      {/* Each side's catch-all sits inside its guard, so a signed-out deep link
          reaches the guard, which sends it to /login with the page to return to. */}
      {/* Both sides. */}
      <Route element={<ProtectedRoute />}>
        <Route path="/profile" element={<ProfilePage />} />
      </Route>
      {/* BOI cockpit: staff and admins (Google SSO). */}
      <Route element={<StaffRoute />}>
        <Route element={<AreaLayout side="BOI cockpit" />}>
          <Route path="/cockpit" element={<CockpitPage />} />
          <Route path="/cockpit/engagements/:engagementId" element={<EngagementPage />} />
          <Route path="/cockpit/engagements/:engagementId/intake" element={<IntakePage />} />
          <Route path="/cockpit/engagements/:engagementId/library" element={<LibraryPage />} />
          <Route path="/cockpit/engagements/:engagementId/coverage" element={<CoveragePage />} />
          <Route path="/cockpit/engagements/:engagementId/draft-flow" element={<DraftFlowPage />} />
          <Route
            path="/cockpit/engagements/:engagementId/sessions/:sessionId/map"
            element={<ContributorMapPage />}
          />
        </Route>
        <Route path="/cockpit/*" element={<NotFoundPage />} />
        <Route element={<AdminRoute />}>
          <Route path="/admin/users" element={<AdminUsersPage />} />
          <Route path="/admin/activity" element={<AdminActivityPage />} />
          <Route path="/admin/workspaces" element={<AdminWorkspacesPage />} />
          <Route path="/admin/workspaces/:workspaceId" element={<AdminWorkspacePage />} />
          <Route element={<AreaLayout side="BOI cockpit" />}>
            <Route path="/admin/nda" element={<AdminNdaPage />} />
          </Route>
          <Route path="/admin/*" element={<NotFoundPage />} />
        </Route>
      </Route>
      {/* Client portal: client users (email + password). */}
      <Route element={<ClientRoute />}>
        <Route element={<AreaLayout side="Client portal" />}>
          <Route path="/portal/nda" element={<PortalNdaPage />} />
          {/* Every other portal page waits for the NDA in force to be accepted. */}
          <Route element={<NdaRoute />}>
            <Route path="/portal" element={<PortalPage />} />
            <Route path="/portal/people" element={<PortalPeoplePage />} />
            <Route path="/portal/engagement" element={<PortalEngagementPage />} />
            <Route path="/portal/documents" element={<PortalDocumentsPage />} />
          </Route>
        </Route>
        <Route path="/portal/*" element={<NotFoundPage />} />
      </Route>
      {/* Contributor area: people interviewed about the process, often on a phone. */}
      <Route element={<ContributorRoute />}>
        <Route
          path="/contribute"
          element={
            <Suspense fallback={<FullPageLoader />}>
              <ContributorArea />
            </Suspense>
          }
        />
        <Route path="/contribute/*" element={<NotFoundPage />} />
      </Route>
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}
